<template>
  <div class="sim-compact" :class="{ 'sim-compact--phone': phone }">
    <!-- Lista de objetos (geometrias, fontes, fluxos) -->
    <transition name="sim-sheet-left">
      <aside v-show="panel === 'objects'" class="sim-sheet sim-sheet--left theme-background">
        <header class="sim-sheet__header">
          <span class="text-subtitle-2">Objects</span>
          <v-btn icon small aria-label="Close" @click="panel = null">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </header>
        <div class="sim-sheet__body">
          <slot name="left" />
        </div>
      </aside>
    </transition>

    <!-- Propriedades: fixas abaixo do canvas com a tela em pé; painel lateral com a tela deitada -->
    <transition :name="docked ? '' : 'sim-sheet-right'">
      <aside
        v-show="docked || panel === 'properties'"
        class="sim-sheet theme-background"
        :class="docked ? 'sim-sheet--docked' : 'sim-sheet--right'"
      >
        <header v-if="!docked" class="sim-sheet__header">
          <span class="text-subtitle-2">Properties</span>
          <v-btn icon small aria-label="Close" @click="panel = null">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </header>
        <div class="sim-sheet__body">
          <slot name="right" />
        </div>
      </aside>
    </transition>

    <nav class="sim-bottom-bar elevation-8">
      <v-btn
        text
        :color="panel === 'objects' ? 'primary' : ''"
        @click="toggle('objects')"
      >
        <v-icon :left="!phone">mdi-layers-outline</v-icon>
        <span v-if="!phone">Objects</span>
      </v-btn>
      <v-btn v-if="fit" icon aria-label="Fit cell to screen" @click="$emit('fit')">
        <v-icon>mdi-fit-to-page-outline</v-icon>
      </v-btn>
      <v-btn
        v-if="!docked"
        text
        :color="panel === 'properties' ? 'primary' : ''"
        @click="toggle('properties')"
      >
        <v-icon :left="!phone">mdi-tune-variant</v-icon>
        <span v-if="!phone">Properties</span>
      </v-btn>
    </nav>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

/**
 * Layout das telas do simulador para celulares e tablets: os painéis laterais
 * do desktop viram painéis deslizantes abertos pela barra inferior.
 */
export default {
  name: "SimulatorCompactPanels",
  props: {
    // Exibe o botão "ajustar à tela" (emite o evento `fit`)
    fit: { type: Boolean, default: true }
  },
  data: () => ({
    panel: null
  }),
  computed: {
    ...mapGetters("simulator", ["selectedToken"]),
    phone() {
      return this.$vuetify.breakpoint.xsOnly;
    },
    // Tela em pé: as propriedades ficam sempre visíveis abaixo do canvas
    docked() {
      const { width, height } = this.$vuetify.breakpoint;
      return height > width;
    }
  },
  watch: {
    // Ao escolher um objeto na lista, mostra direto as propriedades dele
    selectedToken(token) {
      if (!token || this.panel !== "objects") return;
      this.panel = this.docked ? null : "properties";
    },
    // O espaço do canvas muda quando o painel fixo aparece/some
    docked() {
      this.$nextTick(() => this.$emit("fit"));
    }
  },
  mounted() {
    // O canvas é ajustado considerando o painel fixo, que só existe após a montagem
    this.$nextTick(() => this.$emit("fit"));
  },
  methods: {
    toggle(panel) {
      this.panel = this.panel === panel ? null : panel;
    }
  }
};
</script>

<style lang="scss">
$app-bar-height: 48px;
$bottom-bar-height: 56px;
// Manter igual ao usado em pages/Simulator (fitCellToScreen) e pages/Simulator3d/styles.scss
$docked-height: 45vh;

.sim-compact {
  .sim-sheet {
    position: fixed;
    z-index: 6;
    display: flex;
    flex-direction: column;
    box-shadow: 0 0 12px 0 #00000080;

    &--left,
    &--right {
      top: $app-bar-height;
      bottom: $bottom-bar-height;
      width: 88vw;
      max-width: 360px;
    }

    &--left {
      left: 0;
    }

    &--right {
      right: 0;
    }

    &--docked {
      left: 0;
      right: 0;
      bottom: $bottom-bar-height;
      height: $docked-height;
      z-index: 5;
      border-top: 2px solid var(--primary-color, #3eb772);
    }
  }

  .sim-sheet__header {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 8px 4px 16px;
    border-bottom: 1px solid #ffffff1f;
  }

  .sim-sheet__body {
    flex: 1;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;

    // Os painéis foram feitos para colunas de altura total abaixo da app bar
    #side-nav-simulator,
    #RightNavSimulator {
      height: auto !important;
      min-height: 100%;
      padding-top: 0 !important;
      box-shadow: none !important;
      overflow-y: visible !important;
    }

    .v-data-table__wrapper {
      height: auto !important;
      max-height: none !important;
    }
  }

  .sim-bottom-bar {
    position: fixed;
    z-index: 7;
    left: 0;
    right: 0;
    bottom: 0;
    height: $bottom-bar-height;
    padding-bottom: env(safe-area-inset-bottom);
    display: flex;
    align-items: center;
    justify-content: space-around;
    background: var(--v-dark-base, #272727);
  }

  .sim-sheet-left-enter-active,
  .sim-sheet-left-leave-active,
  .sim-sheet-right-enter-active,
  .sim-sheet-right-leave-active {
    transition: transform 0.2s ease;
  }

  .sim-sheet-left-enter,
  .sim-sheet-left-leave-to {
    transform: translateX(-100%);
  }

  .sim-sheet-right-enter,
  .sim-sheet-right-leave-to {
    transform: translateX(100%);
  }
}
</style>
